import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BookShipmentModal from 'src/components/BookShipmentModal';
import ContactModal from 'src/components/ContactModal';
import LineItemsCard from 'src/components/LineItemsCard';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { LocationCard } from 'src/components/Locations';
import MapHeader from 'src/components/MapHeader';
import { ShipmentQuoteNotifications } from 'src/components/Notifications';
import QuoteBanner from 'src/components/QuoteBanner';
import RatePicker from 'src/components/RatePicker';
import emptyImg from 'src/images/empty.svg';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Icon,
  Row,
  useApp
} from 'straightline-ui';
import {
  ACCESSORIAL_CODES,
  isAccepted,
  isExpired,
  PROJECT_44,
  STATUS
} from 'straightline-utils/constants';
import { formatDate, formatDistanceToNowStrict } from 'straightline-utils/time';

export default function ShipmentQuote({ location }) {
  const app = useApp();
  const { id } = useParams();
  const { copied_quote_id: copiedQuoteId } = location.query;
  const {
    result: quote,
    isLoading,
    isError,
    error
  } = app.service('api/shipment-quotes').useGet(id);

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={350} />
      </div>
    );
  }

  if (isError) {
    return <Error error={error} />;
  }

  if (isAccepted(quote)) {
    return <Accepted quote={quote} />;
  }

  if (isExpired(quote)) {
    return <Expired quote={quote} />;
  }

  return <Active quote={quote} copiedQuoteId={copiedQuoteId} />;
}

const Active = ({ quote, copiedQuoteId }) => {
  const history = useHistory();
  const [rate, setRate] = useState(null);

  return (
    <React.Fragment>
      <MapHeaderQuote quote={quote} />
      <Container fluid className="pb-3">
        <Header quote={quote} />
        <Row>
          <Col xl="7">
            <InfoCard quote={quote} />
            <div className="card mb-4">
              <RatePicker
                quote={quote}
                onChange={({ target }) => {
                  setRate(target.rate);
                }}
                value={rate && rate.id}
              />
            </div>
            <LineItemsCard quote={quote} />
            <NotificationItem quote={quote} />
          </Col>
          <Col xl="5">
            <LocationCard shipment={quote} type="pickup" />
            <LocationCard shipment={quote} type="delivery" />
          </Col>
        </Row>
      </Container>
      {quote && rate && (
        <BookShipmentModal
          open={!!rate}
          quote={quote}
          rate={rate}
          copiedQuoteId={copiedQuoteId}
          onCancel={() => {
            setRate(null);
          }}
          onSuccess={(shipment) => {
            history.push(`/shipments/${shipment.id}`);
          }}
        />
      )}
    </React.Fragment>
  );
};

const locationAccessorials = [
  ...ACCESSORIAL_CODES.PICKUP,
  ...ACCESSORIAL_CODES.DELIVERY
];

const InfoCard = ({ quote }) => {
  const additionalAccessorials = quote.accessorial_codes
    ? quote.accessorial_codes.filter((code) => {
        return !locationAccessorials.find(({ value }) => value === code);
      })
    : null;

  return (
    <Card>
      <CardBody>
        <QuoteBanner quote={quote} />
        <div className="mt-3" style={{ fontSize: '1.01rem' }}>
          Select a rate below to view more information, make a price offer, or
          book this shipment. This price includes line-haul & fuel surcharge.
          All delivery dates and times are estimates. For a list of all possible
          accessorial charges, refer to the{' '}
          <a href="/support" target="_blank" rel="noopener noreferrer">
            Support
          </a>{' '}
          page.
        </div>
        {additionalAccessorials?.length > 0 && (
          <div className="mt-3">
            <div className="text-muted small mb-1">Additional Accessorials</div>
            <div>
              {additionalAccessorials.map((code) => {
                const acc = PROJECT_44.ACCESSORIAL_CODES.find(
                  ({ value }) => value === code
                );
                return (
                  <Badge key={code} color="soft-primary" pill className="mr-2">
                    {acc?.label || code + ' (UNKNOWN ACCESSORIAL)'}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const Accepted = ({ quote }) => {
  const app = useApp();
  const { result, isError, error } = app.service('api/shipments').useFind({
    query: { shipment_quote_id: quote.id, $limit: 1 }
  });

  if (isError) {
    return <Error error={error} />;
  }

  const shipment = result && result.data[0];

  return (
    <React.Fragment>
      <MapHeaderQuote quote={quote} />
      <Container fluid className="pb-3">
        <Header quote={quote} />
        <InactiveContent quote={{ ...quote, ...shipment }}>
          <Card>
            <CardBody>
              <QuoteBanner quote={quote} />
              <h2 className="mt-3 mb-3">Congratulations!</h2>
              <p className="text-secondary">
                This quote was accepted on{' '}
                {formatDate(quote.accepted_at, 'LLL d, yyyy')} and is now
                booked! You can view the shipment for up-to-date details,
                notifications, and progress. You can also request a new quote
                with the same information as this quote. If you have any
                questions, please{' '}
                <ContactModal
                  config={{
                    subject: `App Contact - Quote #${quote.id}`,
                    service: 'api/shipment-quotes',
                    service_id: quote.id
                  }}
                >
                  {(toggle) => (
                    <Button
                      color="link"
                      className="p-0 d-inline"
                      onClick={toggle}
                    >
                      Contact Straightline
                    </Button>
                  )}
                </ContactModal>
                .
              </p>
              <div className="mb-n2">
                <Button
                  to="/quote"
                  query={{ copied_quote_id: quote.id }}
                  color="primary"
                  className="mr-3 mb-2"
                >
                  <Icon type="clipboard" className="mr-2" />
                  Request Again
                </Button>
                {shipment && (
                  <Button
                    to={`/shipments/${shipment.id}`}
                    color="primary"
                    className="animated fadeIn fast mb-2"
                  >
                    <Icon type="package" className="mr-2" />
                    View Shipment #{shipment.id}
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </InactiveContent>
      </Container>
    </React.Fragment>
  );
};

const Expired = ({ quote }) => {
  return (
    <React.Fragment>
      <MapHeaderQuote quote={quote} />
      <Container fluid className="pb-3">
        <Header quote={quote} />
        <InactiveContent quote={quote}>
          <Card>
            <CardBody>
              <QuoteBanner quote={quote} />
              <h2 className="mt-3 mb-3">Bummer!</h2>
              <p className="text-secondary">
                This quote is either expired or passed its pickup date. Quotes
                are valid for 24 hours, but you can request a new quote with the
                same information as this quote or updated dates. If you have any
                questions, please{' '}
                <ContactModal
                  config={{
                    subject: `App Contact - Quote #${quote.id}`,
                    service: 'api/shipment-quotes',
                    service_id: quote.id
                  }}
                >
                  {(toggle) => (
                    <Button
                      color="link"
                      className="p-0 d-inline"
                      onClick={toggle}
                    >
                      Contact Straightline
                    </Button>
                  )}
                </ContactModal>
                .
              </p>
              <Button
                to="/quote"
                query={{ copied_quote_id: quote.id }}
                color="primary"
              >
                <Icon type="clipboard" className="mr-2" />
                Request Again
              </Button>
            </CardBody>
          </Card>
        </InactiveContent>
      </Container>
    </React.Fragment>
  );
};

const MapHeaderQuote = ({ quote }) => {
  const shipment = {
    ...quote,
    status_type: STATUS.BOOKED
  };

  return <MapHeader shipment={shipment} />;
};

const Header = ({ quote }) => {
  return (
    <div className="header">
      <div className="header-body">
        <Row className="align-items-end justify-content-between">
          <Col xl="auto" className="mb-3 mb-xl-0">
            <Row className="font-weight-bold">
              <Col xs="auto" className="mb-2">
                <span className="text-muted">QUOTE #: </span>
                {quote.id}
              </Col>
              {quote.reference_number && (
                <Col xs="auto" className="mb-2">
                  <span className="text-muted">REF#: </span>
                  {quote.reference_number}
                </Col>
              )}
              <Col xs="auto" className="mb-2">
                <span className="text-muted">MODE: </span>
                {quote.mode}
              </Col>
              <Col xs="auto" className="mb-2">
                <span className="text-muted">TRUCK: </span>
                {quote.truck.label}
              </Col>
              <Col xs="auto" className="mb-2">
                <span className="text-muted">STATUS: </span>{' '}
                {(() => {
                  if (isAccepted(quote)) {
                    return <span className="text-success">Accepted</span>;
                  }
                  if (isExpired(quote)) {
                    return <span className="text-danger">Expired</span>;
                  }
                  return (
                    <span>
                      expires {formatDistanceToNowStrict(quote.expired_at)}
                    </span>
                  );
                })()}
              </Col>
            </Row>
            <Row className="align-items-center mb-n3">
              <Col xs="auto" className="mb-2 pr-0 align-items-center d-flex">
                <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${quote.pickup_address.city}, ${quote.pickup_address.state}`}</h1>
                <Icon
                  type="arrow-right"
                  style={{ fontSize: 24 }}
                  className="text-success lead d-inline-block font-weight-bold"
                />
              </Col>
              <Col xs="auto" className="mb-2">
                <h1 className="mb-0 font-weight-bold d-inline-block">{`${quote.delivery_address.city}, ${quote.delivery_address.state}`}</h1>
              </Col>
            </Row>
          </Col>
          <Col xl="auto">
            <ContactModal
              config={{
                subject: `App Contact - Quote #${quote.id}`,
                service: 'api/shipment-quotes',
                service_id: quote.id
              }}
            >
              {(toggle) => (
                <Button color="primary" className="mr-3" onClick={toggle}>
                  <Icon type="send" className="mr-2" />
                  Contact
                </Button>
              )}
            </ContactModal>
            <Button
              to="/quote"
              query={{ copied_quote_id: quote.id }}
              color="primary"
              className="mr-3"
            >
              <Icon type="clipboard" className="mr-2" />
              Request Again
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const InactiveContent = ({ quote, children }) => {
  return (
    <React.Fragment>
      <Row>
        <Col xl="7">
          {children}
          <LineItemsCard quote={quote} />
          <div className="card mb-4">
            <RatePicker disabled={true} value={quote.rate_id} quote={quote} />
          </div>
        </Col>
        <Col xl="5">
          <LocationCard shipment={quote} type="pickup" />
          <LocationCard shipment={quote} type="delivery" />
        </Col>
      </Row>
    </React.Fragment>
  );
};

const Error = ({ error }) => {
  return (
    <Container>
      <Row className="align-items-center justify-content-center vh-100">
        <Col lg="6" className="text-center">
          <img
            src={emptyImg}
            alt="Not Found"
            className="img-fluid animated fadeIn faster mb-5"
          />
          {error.code === 404 ? (
            <h1>Could not find that quote</h1>
          ) : (
            <h1>{error.message}</h1>
          )}
          <Button
            to="/shipment-requests"
            color="primary"
            size="lg"
            className="mr-3"
          >
            <Icon type="clipboard" className="mr-2" />
            Quotes
          </Button>
          <Button to="/support" color="secondary" size="lg">
            <Icon type="send" className="mr-2" />
            Contact Straightline
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

function NotificationItem({ quote }) {
  return (
    <Card>
      <CardBody>
        <ShipmentQuoteNotifications quote={quote} />
      </CardBody>
    </Card>
  );
}
